import { AuthenticationType } from 'expo-local-authentication';
import { type Href, router, useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import {
    BodyLargeRegular,
    Button,
    ButtonVariant,
    FingerprintIcon,
    H2Regular,
    Icon,
    isWeb,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import FaceIdIcon from '../../../assets/images/onboarding/face-id-icon.svg';
import { SESSION_DURATION_MINUTES } from '../../auth/constants';
import { useStytch } from '../../auth/hooks/useAuth';
import { deviceAuthenticationTypesAtom } from '../../auth/state/AuthAtoms';
import { OnboardingPromptRoutes } from '../../auth/types/Auth';
import { StorageKeys } from '../../common/constants';
import { isEmpty } from '../../common/misc';
import { useStorage } from '../../common/providers/StorageProvider';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { captureException } from '../../logger/Logger';
import OnboardingFullScreenView from './OnboardingFullScreenView';

const OnboardingBiometrics = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce(`Onboarding/LoginBiometrics`);

    const stytch = useStytch();
    const storage = useStorage();
    const toast = useToastController();
    const deviceAuthenticationTypes = useAtomValue(
        deviceAuthenticationTypesAtom,
    );
    const { callback } = useLocalSearchParams();

    useEffect(() => {
        if (!isWeb) {
            storage.setBool(StorageKeys.HAS_SEEN_BIOMETRICS_PROMPT, true);
        }
    }, []);

    const shouldShowNotificationsPrompt =
        !isWeb &&
        !(storage.getBool(StorageKeys.HAS_SEEN_NOTIFICATIONS_PROMPT) ?? false);

    const goToNextPage = () => {
        let nextPage: Href = '/';
        if (!isEmpty(callback)) {
            nextPage = callback as Href;
        } else if (shouldShowNotificationsPrompt) {
            nextPage = OnboardingPromptRoutes.NOTIFICATIONS;
        }
        router.navigate(nextPage);
    };

    const registerBiometrics = async () => {
        try {
            await stytch.biometrics.register({
                prompt: 'Register your biometric factor',
                allowDeviceCredentials: true,
                allowFallbackToCleartext: true,
            });
            await stytch.biometrics.authenticate({
                sessionDurationMinutes: SESSION_DURATION_MINUTES,
                prompt: 'Sign in to Fruitful',
                allowDeviceCredentials: true,
            });
        } catch (err) {
            toast.show('Error registering biometrics.', {
                duration: 2000,
            });
            captureException(
                new Error('Unable to register biometrics for user'),
            );
        } finally {
            goToNextPage();
        }
    };

    const hasFaceId = deviceAuthenticationTypes?.includes(
        AuthenticationType.FACIAL_RECOGNITION,
    );
    const hasTouchId = deviceAuthenticationTypes?.includes(
        AuthenticationType.FINGERPRINT,
    );
    const authTypeText = hasFaceId
        ? 'Face ID'
        : hasTouchId
          ? 'Touch ID'
          : 'biometrics';
    const text = `We've verified it's you. If you'd like to skip this step in the future, set up ${authTypeText} now.`;
    const buttonText = `Set up ${authTypeText}`;

    return (
        <OnboardingFullScreenView hasHeader={false} scrollable={false}>
            <YStack
                paddingBottom={24}
                flex={1}
                alignItems="center"
                justifyContent="space-between"
            >
                <YStack width="75%" alignItems="center">
                    <H2Regular paddingTop={80} paddingBottom={8}>
                        Success!
                    </H2Regular>
                    <BodyLargeRegular textAlign="center">
                        {text}
                    </BodyLargeRegular>
                    <View
                        marginTop={80}
                        alignSelf="center"
                        width={160}
                        height={160}
                        backgroundColor="$white"
                        opacity={0.7}
                        borderRadius={27}
                        alignItems="center"
                        justifyContent="center"
                    >
                        {hasFaceId ? (
                            <FaceIdIcon height={80} width={80} />
                        ) : (
                            <Icon
                                icon={FingerprintIcon}
                                size={70}
                                color="$black"
                            />
                        )}
                    </View>
                </YStack>
                <YStack width="100%" gap={8} maxWidth={500} alignSelf="center">
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        text={buttonText}
                        onPress={registerBiometrics}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        text="Skip for now"
                        onPress={goToNextPage}
                    />
                </YStack>
            </YStack>
        </OnboardingFullScreenView>
    );
};

export default OnboardingBiometrics;
